<template>
  <div class="address-picker">
    <van-dropdown-menu
      v-if="!focused"
      class="address--top-menu"
      active-color="#3399ff"
    >
      <van-dropdown-item
        v-model="citycode"
        :options="cities"
        @change="handleChangeCity"
      />
    </van-dropdown-menu>

    <div
      v-show="!focused"
      id="mapContainer"
    />

    <van-popup
      :value="true"
      position="bottom"
      class="search__results-wrapper"
      :class="{ full: focused }"
      :overlay="false"
      :closeable="false"
    >

      <van-search
        v-model="placeKeyword"
        class="search__input"
        placeholder="请输入搜索关键词"
        @click="focusSearch"
      />

      <div
        v-show="!focused && showButtons"
        class="buttons__wrapper--bottom"
      >
        <van-button
          @click="handleCancel"
          color="#3399FF"
          plain
          round
        >取消</van-button>
        <van-button
          @click="handleConfirm"
          round
          color="#3399FF"
        >确认</van-button>
      </div>

      <div
        class="search__results"
        :class="{ full: focused }"
      >
        <div
          class="search__results__item"
          :class="{ 'search__results__item--selected': isSelected(item) }"
          v-for="item in searchResults"
          :key="item.id"
          @click="selectAddress(item)"
        >
          <div class="address-info">
            <span class="name">{{ item.name }}</span>
            <span class="address">{{ item.address }}</span>
          </div>

          <van-icon
            v-if="isSelected(item)"
            class="icon--selected"
            name="success"
          />
        </div>
        <van-empty
          v-if="searchResults.length === 0"
          image="search"
          description="请输入详细的省市地名进行搜索"
        />
      </div>
    </van-popup>

    <van-overlay
      :show="showOverlay"
      z-index="10010"
    >
      <div class="loading__wrapper">
        <van-loading
          vertical
          color="#fff"
          type="spinner"
        >定位中...</van-loading>
      </div>
    </van-overlay>
  </div>
</template>

<script>
import { debounce } from '@/utils';
import { Toast } from 'vant';
import { mapState, mapMutations } from 'vuex';
import { PROMOTION_CITIES, DEFAULT_CITY } from './common';
import Share from '@/common/share.js';
import { eventTrackHelper } from './helper/event-tracking';

export default {
  name: 'AddressPicker',

  data() {
    return {
      map: null,
      plugin: {
        placeSearch: null
      },
      citycode: '',
      cities: [
        { text: '深圳市', value: '0755', adcode: '440300', center: [114.085947, 22.547] },
        { text: '广州市', value: '020', adcode: '440100', center: [113.280637, 23.128026] },
        { text: '合肥市', value: '0551', adcode: '340100', center: [117.283042, 31.863722] }
      ],

      focused: false,
      searchLock: false,
      placeKeyword: '',
      searchResults: [],
      selectedAddress: {},
      showOverlay: true,
      showButtons: false
    };
  },

  computed: {
    ...mapState('promotion', {
      channelCompanyId: state => state.vehicleInspection.channelCompanyId,
      positionInfo: state => state.vehicleInspection.position
    })
  },

  watch: {
    placeKeyword: 'handlePlaceSearch'
  },

  mounted() {
    this.initMap();
    this.initShare();
  },

  methods: {
    ...mapMutations('promotion', ['setAddress']),

    initShare() {
      const url = location.href.split('#')[0];
      const shareUrl = url + `#/promotionVehicleInspection/${this.channelCompanyId}`;
      Share.registerWeixinShare(url, shareUrl);
    },

    async initMap() {
      this.map = new AMap.Map('mapContainer', {
        resizeEnable: true,
        zoom: 12,
        center: DEFAULT_CITY.center
      });

      AMap.plugin(
        [
          'AMap.PlaceSearch',
          'AMap.Geolocation',
          'AMap.Geocoder'
        ],
        async() => {
          this.initGeolocation();

          // 若已有定位信息
          if (this.positionInfo && this.positionInfo.citycode) {
            return this.configMap(this.positionInfo);
          } else {
            // 无定位则重新定位
            let position = await this.getLocation().catch(err => Toast(err));

            // 校验定位
            if (
              !position.citycode ||
              !position.center ||
              !PROMOTION_CITIES.includes(position.citycode)
            ) {
              position = DEFAULT_CITY;
            }
            return this.configMap(position);
          }
        }
      );
    },

    configMap(position) {
      this.citycode = position.citycode;
      this.map.setCenter(position.center);
      this.initPlaceSearch(position.citycode);
      this.showOverlay = false;

      // 初始化地址列表
      const keyword = position.address || position.city;
      if (!this.searchLock && keyword) {
        this.handlePlaceSearch(keyword);
      }
    },

    getLocation() {
      const geolocation = this.plugin.geolocation;

      return new Promise((resolve, reject) => {
        // 精确定位
        geolocation.getCurrentPosition((locatePositionStatus, positionRes) => {
          // console.log('精确定位',locatePositionStatus, positionRes)
          if (locatePositionStatus === 'complete') {
            resolve({
              center: [positionRes.position.lng, positionRes.position.lat],
              address: positionRes.formattedAddress,
              citycode: positionRes.addressComponent.citycode
            });
          } else {
            // 若精确定位失败，则定位当前城市
            geolocation.getCityInfo((locateCityStatus, cityRes) => {
              // console.log('定位城市', locateCityStatus, cityRes)
              if (locateCityStatus === 'complete') {
                resolve(cityRes);
              } else {
                reject('定位失败');
              }
            });
          }
        });
      });
    },

    initGeolocation() {
      const geolocation = new AMap.Geolocation({ extensions: 'all' });
      this.map.addControl(geolocation);
      this.plugin.geolocation = geolocation;
      return geolocation;
    },

    initPlaceSearch(citycode) {
      this.plugin.placeSearch = new AMap.PlaceSearch({
        city: citycode, // 兴趣点城市
        autoFitView: true,
        citylimit: true, // 是否强制限制在设置的城市内搜索
        extensions: 'all'
      });
    },

    handlePlaceSearch: debounce(function(keyword) {
      if (!keyword || !this.plugin.placeSearch) return;
      this.plugin.placeSearch.search(keyword, (status, result) => {
        console.log('SEARCH', status, result);
        // reset
        this.selectedAddress = {};
        switch (status) {
          case 'complete':
            this.searchResults = result.poiList.pois;
            break;
          case 'error':
            Toast(result);
            break;

          default:
            this.searchResults = [];
            break;
        }
      });
    }, 300),

    handleChangeCity(citycode) {
      const city = this.cities.find(item => item.value === citycode);
      if (city) {
        this.plugin.placeSearch.setCity(citycode);
        this.placeKeyword = '';
        this.map.setCenter(city.center);
        eventTrackHelper.setCityCode(city.adcode);
        this.handlePlaceSearch(city.text);
      }
    },

    focusSearch() {
      this.focused = true;
      // 点击输入框搜索后设置锁，防止定位成功后覆盖已手动搜索的内容
      this.searchLock = true;
    },

    handleCancel() {
      this.$router.push(`/promotionVehicleInspection/${this.channelCompanyId}`);
    },

    handleConfirm() {
      const info = this.selectedAddress;
      if (!info.id) {
        return Toast({
          message: '请选择一个地址',
          position: 'top'
        });
      }

      this.setAddress(info);
      this.$router.push(`/promotionVehicleInspection/${this.channelCompanyId}`);
    },

    selectAddress(address) {
      this.selectedAddress = address;
      // 搜索框失焦，搜索结果容器下滑
      this.focused = false;
      this.showButtons = true;
      this.markAddress(address);
    },

    isSelected(item) {
      return item.id === this.selectedAddress.id;
    },

    markAddress(address) {
      this.map.clearMap();
      const marker = new AMap.Marker({
        position: [address.location.lng, address.location.lat],
        title: address.name
      });
      this.map.add(marker);
      this.map.setZoomAndCenter(15, marker.getPosition());
    }
  }
};
</script>

<style lang="scss" scoped>
.address-picker {
  position: relative;

  min-height: 100vh;

  $height-results-wrapper: 5.6rem;
  $height-dropmenu: .84rem;
  $height-search: 1.08rem;
  $color-primary: #3e9eff;
  $height-map: 260px;

  @mixin one-line-overflow {
    overflow : hidden;

    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .address--top-menu {
    height: $height-dropmenu;
  }

  #mapContainer {
    width: 100vw;
    height: $height-map;
  }

  .search {
    &__results {
      overflow-y: scroll;

      height: calc(100vh - #{$height-map} - #{$height-dropmenu} - #{$height-search});

      transition-duration: .3s;

      &.full {
        height: calc(100vh - #{$height-search});
      }

      &-wrapper {
        position: relative;

        height: calc(100vh - #{$height-map} - #{$height-dropmenu});
        &.full {
          height: 100vh;
        }
        .buttons__wrapper--bottom {
          position: absolute;
          z-index: 3000;
          bottom: 0;
          left: 0;

          display: flex;
          align-items: center;
          justify-content: space-around;

          width: 100vw;
          height: 1.2rem;

          transition-duration: .3rem;

          background: #fff;
          box-shadow: 0px 0px 12px 0px rgba(0,0,0,0.1);

          .van-button {
            width: 3.2rem;

            font-size: .32rem;
          }
        }

        .btns__wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;

          height: 0.96rem;

          font-size: 0.3rem;
          .btn--confirm, .btn--cancel {
            display: inline-block;

            padding: 0.34rem;

            color: #383838;
          }
          .btn--confirm {
            color: $color-primary;
          }
        }
      }

      &__item {
        display: flex;
        align-items: center;
        justify-content: space-between;

        box-sizing: border-box;
        height: 1.3rem;
        padding: 0.2rem 0.32rem;

        cursor: pointer;

        box-shadow: inset 0px -1px 0px 0px rgba(230,230,230,1);

        &:nth-last-child(1) {
          margin-bottom: 1.2rem;
        }

        &--selected {
          background: #e9f4ff;
        }

        .icon--selected {
          color: $color-primary;

          font-size: 0.48rem;
        }

        .address-info {
          display: flex;
          flex-direction: column;

          .name {
            max-width: 6rem;
            margin-bottom: 0.14rem;

            letter-spacing: 0;

            color: #383838;

            font-family: PingFangSC-Medium;
            font-size: 0.32rem;
            font-weight: 500;

            @include one-line-overflow;
          }

          .address {
            max-width: 6rem;

            letter-spacing: 0;

            color: #808080;

            font-size: 0.24rem;
            font-weight: 400;

            @include one-line-overflow;
          }
        }

      }
    }

    &__input {
      height: $height-search;
    }
  }

  .loading__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;

    height: 100%;
  }
}
</style>
<style lang="scss">
.address--top-menu {
  .van-dropdown-menu__bar {
    height: 100%;
  }
  .van-dropdown-menu__item  {
    justify-content: flex-start;
  }
}
</style>
